import React, { useState } from 'react'
import { Eye, EyeSlash, PencilSimple, FloppyDisk, X, CircleNotch, CheckCircle, WarningCircle, Trash } from '@phosphor-icons/react'
import { Tooltip } from '@/components/ui/Tooltip'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { cn } from '@/lib/utils'
import { labelClass, labelStyle, inputClass, inputStyle } from '@/lib/styleKit'
import type { LLMKeyTestResult } from '@/types/settings'

interface MaskedFieldProps {
  label: string
  isSet: boolean
  last4?: string
  placeholder?: string
  /** Stage the new value (used when no onCommit is provided). Optional: a screen
   *  that persists immediately via onCommit has nothing to stage. */
  onSave?: (value: string) => void
  /** Delete the stored value. Confirmed first, and awaited if it returns a promise. */
  onClear?: () => void | Promise<void>
  /** Tooltip on the delete button. Defaults to "Delete key". */
  clearTooltip?: string
  /** Body of the delete confirmation — say what happens after, e.g. that the
   *  platform key takes over. */
  clearDescription?: React.ReactNode
  /**
   * Confirm button in the delete dialog. Defaults to "Delete key", which is only
   * true where `onClear` deletes immediately. A caller that merely *stages* the
   * removal until a later save must say so ("Remove on save") — otherwise the
   * button contradicts the dialog body it sits under.
   */
  clearConfirmLabel?: string
  /**
   * Verify the typed value against the provider. When provided, a "Test" button
   * appears and saving is blocked until the current value has been tested — a
   * key that only fails at send time is the expensive way to find out it's wrong.
   * An 'unreachable' verdict still allows the save: we couldn't prove the key is
   * bad, and a network problem must not lock the user out of their own settings.
   */
  onTest?: (value: string) => Promise<LLMKeyTestResult>
  /** Label for the test button, e.g. "Test OpenAI". Defaults to "Test key". */
  testLabel?: string
  /**
   * Fetch the current decrypted value from the backend so the input can be
   * pre-filled when the user enters edit mode. If omitted, edit starts blank.
   */
  onReveal?: () => Promise<string>
  /**
   * Persist the new value directly (e.g. to the DB). When provided, the save
   * action awaits this and shows a spinner instead of staging via onSave.
   */
  onCommit?: (value: string) => Promise<void>
  /** Hides the edit/delete controls, leaving only the masked read-only
   *  view -- for callers whose role has view-only access (e.g. Manager on
   *  integrations.manage). Defaults to false; every existing caller is
   *  unaffected. */
  readOnly?: boolean
}

const MaskedField: React.FC<MaskedFieldProps> = ({ label, isSet, last4, placeholder, onSave, onClear, onReveal, onCommit, onTest, testLabel, clearTooltip, clearDescription, clearConfirmLabel, readOnly = false }) => {
  // Stable per-instance id so the visible label owns the input's accessible
  // name. Same idiom as ui/Input, and safe when several MaskedFields on one
  // page share a label string (e.g. two integrations both showing "API Key").
  const fieldId = `masked-field-${React.useId()}`
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState('')
  const [showValue, setShowValue] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [error, setError] = useState('')
  // Pinned to the exact value that was tested, so editing the key after a pass
  // invalidates the verdict instead of letting an untested value through.
  const [verdict, setVerdict] = useState<{ value: string; result: LLMKeyTestResult } | null>(null)

  const trimmed = value.trim()
  const freshVerdict = verdict && verdict.value === trimmed ? verdict.result : null
  const passedTest = freshVerdict !== null && freshVerdict.status !== 'invalid'
  const canSave = Boolean(trimmed) && !saving && !testing && !revealing && (!onTest || passedTest)

  const beginEdit = async () => {
    setIsEditing(true)
    setValue('')
    setVerdict(null)
    // Pre-fill with the existing key from the DB when available.
    if (onReveal && isSet) {
      setRevealing(true)
      try {
        const existing = await onReveal()
        setValue(existing || '')
      } catch {
        // Leave the field blank — the user can still type a new value.
      } finally {
        setRevealing(false)
      }
    }
  }

  const handleClear = async () => {
    if (!onClear || clearing) return
    setClearing(true)
    try {
      await onClear()
      setConfirmingClear(false)
    } catch {
      // Keep the dialog open so the user can retry; the store surfaces the error.
    } finally {
      setClearing(false)
    }
  }

  const handleTest = async () => {
    if (!onTest || !trimmed || testing) return
    setError('')
    setTesting(true)
    try {
      setVerdict({ value: trimmed, result: await onTest(trimmed) })
    } catch {
      setVerdict({
        value: trimmed,
        result: { status: 'unreachable', success: false, message: 'Could not run the key test.' },
      })
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!trimmed || saving) return
    if (onTest && !passedTest) {
      setError('Test this key before saving it.')
      return
    }
    // Never persist the masked placeholder itself. The unmasked display renders
    // the value as bullet characters (U+2022); if that ever lands back in the
    // input and gets submitted, the stored "key" is a row of bullets that the
    // OpenAI/Anthropic SDK can't ASCII-encode into the auth header, crashing
    // every send. Real keys/secrets are ASCII and never contain bullets.
    if (trimmed.includes('•')) {
      setError('That looks like the masked value, not a real key. Type the actual key.')
      return
    }
    setError('')
    if (onCommit) {
      setSaving(true)
      try {
        await onCommit(trimmed)
        setValue('')
        setVerdict(null)
        setIsEditing(false)
        setShowValue(false)
      } catch {
        // Keep edit mode open so the user can retry; parent surfaces the error.
      } finally {
        setSaving(false)
      }
    } else {
      onSave?.(trimmed)
      setValue('')
      setVerdict(null)
      setIsEditing(false)
      setShowValue(false)
    }
  }

  const handleCancel = () => {
    setValue('')
    setVerdict(null)
    setIsEditing(false)
    setShowValue(false)
    setError('')
  }

  if (isEditing) {
    return (
      <div>
        <label htmlFor={fieldId} className={labelClass} style={labelStyle}>{label}</label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              id={fieldId}
              type={showValue ? 'text' : 'password'}
              name="api-key-value"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error) setError('') }}
              // A stale verdict is discarded implicitly: freshVerdict compares
              // against the current value, so an edit un-passes the test.
              placeholder={revealing ? 'Loading current value…' : (placeholder || `Enter ${label.toLowerCase()}`)}
              className={cn(inputClass, 'pr-10')}
              style={inputStyle}
              disabled={revealing || saving}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
            />
            <button
              type="button"
              onClick={() => setShowValue(!showValue)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-[#8a8f98] hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors"
              aria-label={showValue ? 'Hide value' : 'Show value'}
            >
              {showValue ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Tooltip label={onTest && !passedTest ? 'Test the key first' : 'Save'}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="h-9 w-9 flex items-center justify-center rounded-md bg-[#5e6ad2] text-white hover:bg-[#7170ff] active:bg-[#5058c0] transition-colors duration-150 disabled:opacity-50"
              aria-label="Save"
            >
              {saving
                ? <CircleNotch className="w-4 h-4 animate-spin" weight="bold" />
                : <FloppyDisk className="w-4 h-4" weight="bold" />}
            </button>
          </Tooltip>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground dark:text-[#8a8f98] hover:text-foreground dark:hover:text-[#f7f8f8] hover:bg-muted/50 dark:hover:bg-white/[0.04] transition-colors disabled:opacity-50"
            aria-label="Cancel"
          >
            <X className="w-4 h-4" weight="bold" />
          </button>
        </div>
        {onTest && (
          <button
            type="button"
            onClick={handleTest}
            disabled={!trimmed || testing || saving || revealing}
            className="mt-2 h-9 px-3 flex items-center gap-1.5 rounded-md border border-border/60 dark:border-white/[0.08] text-foreground dark:text-[#f7f8f8] hover:border-border/80 dark:hover:border-white/[0.14] hover:bg-muted/50 dark:hover:bg-white/[0.04] transition-colors duration-150 disabled:opacity-50 disabled:hover:border-border/60 disabled:cursor-not-allowed"
            style={inputStyle}
          >
            {testing
              ? <CircleNotch className="w-4 h-4 animate-spin" weight="bold" />
              : <CheckCircle className="w-4 h-4" weight="bold" />}
            {testing ? 'Testing…' : (testLabel || 'Test key')}
          </button>
        )}
        {error && (
          <p className="text-red-600 dark:text-red-400 mt-1.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
            {error}
          </p>
        )}
        {freshVerdict && !error && (
          <p
            className={`mt-1.5 flex items-start gap-1.5 ${
              freshVerdict.status === 'valid'
                ? 'text-emerald-600 dark:text-emerald-400'
                : freshVerdict.status === 'unreachable'
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-red-600 dark:text-red-400'
            }`}
            style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
          >
            {freshVerdict.status === 'valid'
              ? <CheckCircle className="w-3.5 h-3.5 mt-px shrink-0" weight="fill" />
              : <WarningCircle className="w-3.5 h-3.5 mt-px shrink-0" weight="fill" />}
            <span>
              {freshVerdict.message}
              {freshVerdict.status === 'unreachable' && ' You can still save it.'}
            </span>
          </p>
        )}
        {onTest && !freshVerdict && !error && (
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1.5" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
            Test the key to enable saving.
          </p>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Read-only view: there is no control to label yet (the field becomes an
          input only in edit mode), so this is a caption, not a <label> — a
          <label> with no associated control has no accessible target. */}
      <span className={labelClass} style={labelStyle}>{label}</span>
      <div className="flex items-center gap-2">
        <div
          className="flex-1 h-9 px-3 bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] rounded-md flex items-center"
          style={inputStyle}
        >
          {isSet ? (
            <span className="text-muted-foreground dark:text-[#8a8f98] font-mono">
              {'•'.repeat(12)}{last4 ? ` ${last4}` : ''}
            </span>
          ) : (
            <span className="text-muted-foreground/60 dark:text-[#62666d] italic">Not configured</span>
          )}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={beginEdit}
            className="h-9 w-9 flex items-center justify-center text-muted-foreground dark:text-[#8a8f98] hover:text-[#5e6ad2] dark:hover:text-[#a5b4fc] hover:bg-muted/50 dark:hover:bg-white/[0.04] rounded-md transition-colors duration-150"
            title={isSet ? 'Change' : 'Set'}
            aria-label={isSet ? 'Change' : 'Set'}
          >
            <PencilSimple className="w-4 h-4" />
          </button>
        )}
        {!readOnly && isSet && onClear && (
          <Tooltip label={clearTooltip || 'Delete key'}>
            <button
              type="button"
              onClick={() => setConfirmingClear(true)}
              disabled={clearing}
              className="h-9 w-9 flex items-center justify-center text-muted-foreground dark:text-[#8a8f98] hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/[0.08] dark:hover:bg-red-500/[0.10] rounded-md transition-colors duration-150 disabled:opacity-50"
              aria-label={clearTooltip || 'Delete key'}
            >
              {clearing
                ? <CircleNotch className="w-4 h-4 animate-spin" weight="bold" />
                : <Trash className="w-4 h-4" />}
            </button>
          </Tooltip>
        )}
      </div>
      {/* Deleting a credential is not undoable — the plaintext is gone from the
          DB and can only be restored by pasting it again. */}
      <ConfirmDialog
        open={confirmingClear}
        title={`Delete ${label}?`}
        description={clearDescription || 'This removes the stored key from your account. You can add it again at any time.'}
        confirmLabel={clearConfirmLabel || 'Delete key'}
        tone="destructive"
        loading={clearing}
        onConfirm={handleClear}
        onCancel={() => setConfirmingClear(false)}
      />
    </div>
  )
}

export default MaskedField
